"use client";

import * as React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "faq-1",
  question: "How are transfer prices calculated?",
  answer: "Every price is fixed per vehicle, not per passenger. What you see is the exact total you pay, with no surge fees or hidden additions."
}, {
  id: "faq-2",
  question: "When and how do I pay for my transfer?",
  answer: "Pay your agreed fare in cash when you arrive at your hotel. We accept payment in any currency. Please confirm the currency and agreed amount with us before your transfer."
}, {
  id: "faq-3",
  question: "What happens if my flight to Hurghada is delayed?",
  answer: "We monitor your flight's arrival time and any delays free of charge. Your transfer includes up to 60 minutes of free waiting time from the agreed meeting time. Please share your flight number when you book and message us on WhatsApp if your flight is delayed or if you expect a longer delay."
}, {
  id: "faq-4",
  question: "Where do I meet my driver at Hurghada Airport?",
  answer: "Your driver waits directly outside the arrivals terminal exit doors holding a personalized sign with your name clearly displayed."
}, {
  id: "faq-5",
  question: "Can I book a return transfer back to Hurghada Airport?",
  answer: "Yes. You can book round-trip transfers in advance or arrange your hotel departure pickup easily with our dispatch team over WhatsApp."
}, {
  id: "faq-6",
  question: "Do you provide child seats?",
  answer: "Child seats are available free of charge for children aged one year and older. Please include your child's age in your transfer request."
}, {
  id: "faq-7",
  question: "How do I choose the right vehicle for my luggage?",
  answer: "Tell us your passenger count and the number and size of your bags, including any sports equipment, so we can confirm a suitable vehicle."
}, {
  id: "faq-8",
  question: "Are highway tolls, parking fees, and waiting times included?",
  answer: "Yes. Every route fare includes highway tolls and airport parking fees. In addition, flight monitoring is free of charge and your transfer includes up to 60 minutes of free waiting time from the agreed meeting time."
}, {
  id: "faq-9",
  question: "Can I book or ask questions directly on WhatsApp?",
  answer: "Our team is available 24/7 to answer your questions and coordinate your pickup on WhatsApp at +20 10 10634627."
}, {
  id: "faq-10",
  question: "How far in advance should I reserve my transfer?",
  answer: "We recommend reserving at least 12 to 24 hours in advance, although we also accommodate last-minute transfers whenever vehicle availability allows."
}];
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  return <section id="faq" data-nav="light" ref={ref} className="bg-secondary text-secondary-foreground py-20 md:py-24 border-t border-border transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-[1136px] mx-auto px-6">
        {/* Header Block with Signature Kit Device */}
        <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">
              Got Questions?
            </span>
            {/* Signature hairline rule capped with a crisp turquoise terminal dot */}
            <div className="flex items-center" aria-hidden="true">
              <span className="w-8 h-px bg-border inline-block" />
              <span className="size-1.5 rounded-full bg-primary inline-block shrink-0" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-black">
            Frequently Asked Questions
          </h2>
        </div>

        {/* 10-Item Accordion container centered in narrative columns 2-11 */}
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {FAQ_ITEMS.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="bg-card text-card-foreground border border-border rounded-md px-5 sm:px-6 shadow-2xs data-[state=open]:border-primary transition-colors">
                <AccordionTrigger className="text-base sm:text-lg font-medium text-foreground py-5 hover:no-underline text-left cursor-pointer group">
                  <span className="pr-4 leading-snug group-hover:text-primary transition-colors">
                    {item.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-5 pt-1">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}